import React, { useMemo } from 'react';
import { Button, Empty, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, FileSearchOutlined, GlobalOutlined, ReloadOutlined, ShopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { BrandResearchEntry, BrandResearchKeyword } from '../../types/brandResearch';
import WorkspaceModal from '../Layout/WorkspaceModal';
import { filterBrandRiskNegativeKeywords } from '../../utils/brandResearchKeywords';

const statusColorMap: Record<string, string> = {
  COMPLETED: 'green',
  FAILED: 'red',
  IN_PROGRESS: 'blue',
  PARTIAL_SUCCESS: 'gold',
  PENDING: 'default',
};

const formatTimestamp = (value?: { toDate?: () => Date } | null): string => {
  if (!value || typeof value.toDate !== 'function') return '—';
  return dayjs(value.toDate()).format('YYYY-MM-DD HH:mm');
};

const intentTypeLabels: Record<string, string> = {
  category_anchor: 'Category Anchors',
  brand_category: 'Brand + Category',
  feature: 'Features',
  finish_material_style: 'Finish / Material / Style',
  use_case_audience: 'Use Case / Audience',
  commercial_modifier: 'Commercial Modifiers',
  negative_exclusion: 'Negative Exclusions',
};

const positiveIntentOrder = [
  'category_anchor',
  'brand_category',
  'feature',
  'finish_material_style',
  'use_case_audience',
  'commercial_modifier',
];

type KeywordVolumeChannel = 'amazon' | 'google';

const keywordVolume = (
  keyword: BrandResearchKeyword,
  volumeChannel?: KeywordVolumeChannel,
) => {
  if (volumeChannel === 'amazon') {
    return keyword.amazonSearchVolume ?? keyword.searchVolume;
  }
  if (volumeChannel === 'google') {
    return keyword.googleSearchVolume ?? keyword.searchVolume;
  }
  return keyword.searchVolume;
};

const renderKeywordTags = (
  keywords: BrandResearchKeyword[],
  limit = 5,
  volumeChannel?: KeywordVolumeChannel,
) => {
  if (!keywords.length) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }

  const hiddenKeywords = keywords.slice(limit);

  return (
    <Space size={[4, 8]} wrap>
      {keywords.slice(0, limit).map((keyword) => (
        <Tag
          key={`${keyword.keyword}-${(keyword.sources || []).join('-')}`}
          className="brand-research-keyword-chip brand-research-keyword-chip--priority"
        >
          {keyword.keyword}
          {typeof keywordVolume(keyword, volumeChannel) === 'number'
            ? ` (${keywordVolume(keyword, volumeChannel)})`
            : ''}
        </Tag>
      ))}
      {hiddenKeywords.length > 0 && (
        <Tooltip
          title={(
            <Space size={[4, 8]} wrap>
              {hiddenKeywords.map((keyword) => (
                <Tag
                  key={`${keyword.keyword}-${(keyword.sources || []).join('-')}-overflow`}
                  className="brand-research-keyword-chip brand-research-keyword-chip--priority"
                >
                  {keyword.keyword}
                  {typeof keywordVolume(keyword, volumeChannel) === 'number'
                    ? ` (${keywordVolume(keyword, volumeChannel)})`
                    : ''}
                </Tag>
              ))}
            </Space>
          )}
        >
          <Tag className="brand-research-keyword-chip" style={{ cursor: 'pointer' }}>
            +{hiddenKeywords.length} more
          </Tag>
        </Tooltip>
      )}
    </Space>
  );
};

const renderSeedTags = (keywords: string[]) => {
  if (!keywords.length) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }

  return (
    <Space size={[4, 8]} wrap>
      {keywords.map((keyword) => (
        <Tag key={keyword} className="brand-research-keyword-chip">
          {keyword}
        </Tag>
      ))}
    </Space>
  );
};

const renderGroupedKeywordTags = (keywords: BrandResearchKeyword[]) => {
  if (!keywords.length) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }

  const groups = keywords.reduce<Record<string, BrandResearchKeyword[]>>((acc, keyword) => {
    const intentType = keyword.intentType || 'category_anchor';
    acc[intentType] = [...(acc[intentType] || []), keyword];
    return acc;
  }, {});

  const orderedIntentTypes = [
    ...positiveIntentOrder.filter((intentType) => groups[intentType]?.length),
    ...Object.keys(groups).filter((intentType) => !positiveIntentOrder.includes(intentType)),
  ];

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {orderedIntentTypes.map((intentType) => (
        <div key={intentType}>
          <Typography.Text type="secondary">
            {intentTypeLabels[intentType] || intentType}
          </Typography.Text>
          <div style={{ marginTop: 6 }}>
            {renderKeywordTags(groups[intentType], 12)}
          </div>
        </div>
      ))}
    </Space>
  );
};

const preferredKeywords = (record: BrandResearchEntry) => (
  record.canonicalKeywords?.length ? record.canonicalKeywords : record.priorityKeywords || []
);

type BrandResearchHistoryModalProps = {
  open: boolean;
  brandName?: string;
  entries: BrandResearchEntry[];
  deletingEntryId?: string | null;
  restartingEntryId?: string | null;
  onDelete?: (entry: BrandResearchEntry) => void;
  onRestart?: (entry: BrandResearchEntry) => void;
  onCancel: () => void;
};

const BrandResearchHistoryModal: React.FC<BrandResearchHistoryModalProps> = ({
  open,
  brandName,
  entries,
  deletingEntryId,
  restartingEntryId,
  onDelete,
  onRestart,
  onCancel,
}) => {
  const title = (
    <div className="brand-research-modal-title">
      <Tag color="cyan">HISTORY</Tag>
      <div className="brand-research-modal-title-main">
        {brandName ? `${brandName} keyword research` : 'Keyword research history'}
      </div>
      <div className="brand-research-modal-title-meta">
        <span className="brand-research-modal-title-meta-item">
          <FileSearchOutlined />
          {entries.length} {entries.length === 1 ? 'doc' : 'docs'}
        </span>
      </div>
    </div>
  );

  const columns = useMemo(() => ([
    {
      title: 'Category',
      dataIndex: 'categoryDisplayName',
      key: 'categoryDisplayName',
      render: (value: string) => (
        <Typography.Text strong className="brand-research-history-category">
          {value || '—'}
        </Typography.Text>
      ),
    },
    {
      title: 'Locale',
      key: 'locale',
      render: (_: unknown, record: BrandResearchEntry) => (
        <Space size={6} wrap>
          <Tag className="brand-research-locale-chip" icon={<ShopOutlined />}>
            {record.marketplace?.toUpperCase() || '—'}
          </Tag>
          <Tag className="brand-research-locale-chip" icon={<GlobalOutlined />}>
            {record.language || 'n/a'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: BrandResearchEntry) => (
        <Tag color={statusColorMap[record.status] || 'default'}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: 'Top Keywords',
      key: 'priorityKeywords',
      render: (_: unknown, record: BrandResearchEntry) => (
        renderKeywordTags(preferredKeywords(record))
      ),
    },
    {
      title: 'Updated',
      key: 'updatedAt',
      width: 170,
      render: (_: unknown, record: BrandResearchEntry) => formatTimestamp(record.updatedAt),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      render: (_: unknown, record: BrandResearchEntry) => (
        <Space size={4}>
          <Tooltip title="Restart research">
            <Button
              type="text"
              icon={<ReloadOutlined />}
              loading={restartingEntryId === record.id}
              disabled={!onRestart || deletingEntryId === record.id}
              aria-label="Restart research"
              onClick={() => onRestart?.(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete research?"
            description="This removes this keyword research document. Existing product keywords stay unchanged."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete?.(record)}
            disabled={!onDelete}
          >
            <Tooltip title="Delete research">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deletingEntryId === record.id}
                disabled={!onDelete || restartingEntryId === record.id}
                aria-label="Delete research"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]), [deletingEntryId, onDelete, onRestart, restartingEntryId]);

  return (
    <WorkspaceModal
      className="brand-research-run-modal brand-research-history-modal"
      open={open}
      title={title}
      onClose={onCancel}
      footer={(
        <div className="brand-research-modal-footer">
          <Button type="primary" onClick={onCancel}>Close</Button>
        </div>
      )}
    >
      <div className="brand-research-history-scroll">
        {entries.length === 0 ? (
          <div className="brand-research-history-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No research documents found for this brand."
            />
          </div>
        ) : (
          <div className="brand-research-history-panel">
            <Table
              rowKey="id"
              className="brand-research-history-table"
              columns={columns}
              dataSource={entries}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              expandable={{
                expandedRowRender: (record: BrandResearchEntry) => (
                  <div className="brand-research-history-expanded">
                    <section className="brand-research-history-detail brand-research-history-detail--wide">
                      <span className="brand-research-panel-eyebrow">Summary</span>
                      <Typography.Paragraph type="secondary">
                        {record.summary || '—'}
                      </Typography.Paragraph>
                    </section>
                    <section className="brand-research-history-detail">
                      <span className="brand-research-panel-eyebrow">Research Seeds</span>
                      <div className="brand-research-history-detail-content">
                        {renderSeedTags(record.seedKeywords || [])}
                      </div>
                    </section>
                    {Boolean(record.amazonKeywords?.length || record.googleKeywords?.length) && (
                      <section className="brand-research-history-detail brand-research-history-detail--wide">
                        <span className="brand-research-panel-eyebrow">Channel Recommendations</span>
                        <div className="brand-research-history-detail-content brand-research-history-channel-grid">
                          <div>
                            <Typography.Text type="secondary">
                              <ShopOutlined /> Amazon marketplace
                            </Typography.Text>
                            <div className="brand-research-history-channel-keywords">
                              {renderKeywordTags(record.amazonKeywords || [], 10, 'amazon')}
                            </div>
                          </div>
                          <div>
                            <Typography.Text type="secondary">
                              <GlobalOutlined /> Google / web SEO
                            </Typography.Text>
                            <div className="brand-research-history-channel-keywords">
                              {renderKeywordTags(record.googleKeywords || [], 10, 'google')}
                            </div>
                          </div>
                        </div>
                      </section>
                    )}
                    <section className="brand-research-history-detail">
                      <span className="brand-research-panel-eyebrow">Negative Keywords</span>
                      <div className="brand-research-history-detail-content">
                        {renderKeywordTags(filterBrandRiskNegativeKeywords(record.negativeKeywords), 8)}
                      </div>
                    </section>
                    <section className="brand-research-history-detail brand-research-history-detail--wide">
                      <span className="brand-research-panel-eyebrow">Priority Keywords</span>
                      <div className="brand-research-history-detail-content">
                        {renderGroupedKeywordTags(preferredKeywords(record))}
                      </div>
                    </section>
                    {record.lastError && (
                      <section className="brand-research-history-detail brand-research-history-detail--wide">
                        <span className="brand-research-panel-eyebrow">Last Error</span>
                        <Typography.Text type="danger">{record.lastError}</Typography.Text>
                      </section>
                    )}
                  </div>
                ),
                rowExpandable: (record: BrandResearchEntry) => Boolean(
                  record.summary
                  || record.seedKeywords?.length
                  || filterBrandRiskNegativeKeywords(record.negativeKeywords).length
                  || preferredKeywords(record).length
                  || record.amazonKeywords?.length
                  || record.googleKeywords?.length
                  || record.lastError
                ),
              }}
            />
          </div>
        )}
      </div>
    </WorkspaceModal>
  );
};

export default BrandResearchHistoryModal;
